import { Big } from 'big.js';
/**
 * Decimal money policy (ADR-003, skill finance-precision).
 *
 * Rules enforced here:
 * - zero float: all arithmetic goes through big.js `Big`;
 * - rounding mode is always an explicit PARAMETER, never a silent global;
 * - currency travels with the amount; mixing currencies throws loudly
 *   (`CurrencyMismatchError`), never converts implicitly;
 * - allocation distributes the remainder explicitly (largest-remainder method),
 *   so `sum(parts) === total` down to the minimal unit — never divide-per-part;
 * - API boundaries use strings (`toApiString` / `parseApiAmount`), never JSON numbers.
 */
// Internal division precision for allocation shares. Every public call site
// still passes an explicit rounding mode where rounding happens.
Big.DP = 40;
const ROUNDING_MODE_TO_BIG = {
    'half-even': 2, // Big.roundHalfEven
    'half-up': 1, // Big.roundHalfUp
    down: 0, // Big.roundDown
    up: 3, // Big.roundUp
};
export function toBigRoundingMode(mode) {
    return ROUNDING_MODE_TO_BIG[mode];
}
export class CurrencyMismatchError extends Error {
    left;
    right;
    constructor(left, right) {
        super(`Currency mismatch: ${left} vs ${right} — convert explicitly via FX service`);
        this.left = left;
        this.right = right;
        this.name = 'CurrencyMismatchError';
    }
}
export function money(amount, currency) {
    return { amount: new Big(amount), currency };
}
export function assertSameCurrency(a, b) {
    if (a.currency !== b.currency) {
        throw new CurrencyMismatchError(a.currency, b.currency);
    }
}
export function roundAmount(amount, scale, mode) {
    return amount.round(scale, toBigRoundingMode(mode));
}
export function roundMoney(m, scale, mode) {
    return { amount: roundAmount(m.amount, scale, mode), currency: m.currency };
}
export function addMoney(a, b) {
    assertSameCurrency(a, b);
    return { amount: a.amount.plus(b.amount), currency: a.currency };
}
export function subMoney(a, b) {
    assertSameCurrency(a, b);
    return { amount: a.amount.minus(b.amount), currency: a.currency };
}
/** Multiply by a dimensionless factor, rounding explicitly at the domain boundary. */
export function mulMoney(m, factor, scale, mode) {
    return { amount: roundAmount(m.amount.times(factor), scale, mode), currency: m.currency };
}
/**
 * Allocate `total` across weighted parts without ever dividing-per-part:
 * each part gets its exact share floored to `scale` decimal places, then the
 * remaining minimal units are distributed one-by-one to the largest fractional
 * parts (ties broken by position). Invariant: sum(parts) === total exactly.
 *
 * `scale` is an explicit PARAMETER (currency exponent / money column scale),
 * never a silent global — see skill finance-precision. `total` must be exactly
 * representable at `scale` decimal places (it always is for NUMERIC(19,4) money).
 */
export function allocate(total, weights, scale) {
    if (!Number.isInteger(scale) || scale < 0) {
        throw new RangeError('allocate scale must be a non-negative integer');
    }
    if (weights.length === 0) {
        throw new RangeError('allocate requires at least one weight');
    }
    if (weights.some((w) => w.lt(0))) {
        throw new RangeError('allocate weights must be non-negative');
    }
    const sumWeights = weights.reduce((acc, w) => acc.plus(w), new Big(0));
    if (sumWeights.eq(0)) {
        throw new RangeError('allocate weights must sum to a positive value');
    }
    // Minimal monetary unit at `scale` decimal places, e.g. scale 2 -> 0.01.
    const unit = new Big(1).div(new Big(10).pow(scale));
    const roundedTotal = total.amount.round(scale, Big.roundDown);
    const exact = weights.map((w) => roundedTotal.times(w).div(sumWeights));
    const base = exact.map((share) => share.round(scale, Big.roundDown));
    let remainder = roundedTotal.minus(base.reduce((acc, s) => acc.plus(s), new Big(0)));
    // Largest-remainder distribution of leftover minimal units.
    const order = exact
        .map((share, index) => ({ index, frac: share.minus(base[index]) }))
        .sort((a, b) => {
        const cmp = b.frac.cmp(a.frac);
        return cmp !== 0 ? cmp : a.index - b.index;
    });
    const parts = [...base];
    for (const { index } of order) {
        if (remainder.lt(unit))
            break;
        parts[index] = parts[index].plus(unit);
        remainder = remainder.minus(unit);
    }
    if (!remainder.eq(0)) {
        // Unreachable when weights are valid; fail loudly rather than leak a cent.
        throw new Error(`allocate left a remainder of ${remainder.toString()}`);
    }
    return parts.map((amount) => ({ amount, currency: total.currency }));
}
/** API boundary: money leaves the system as a plain decimal string. */
export function toApiString(m) {
    return m.amount.toFixed();
}
const API_AMOUNT_PATTERN = /^-?\d+(\.\d{1,18})?$/;
/** API boundary: parse and validate an amount string coming from JSON. */
export function parseApiAmount(raw) {
    if (!API_AMOUNT_PATTERN.test(raw)) {
        throw new RangeError(`Invalid API amount string: "${raw}"`);
    }
    return new Big(raw);
}
//# sourceMappingURL=money.js.map